/**
 * LeaderboardPage.js
 *
 * /fly/custom-declaration-result (and /fly/leaderboard) — the TV for Unclaimed Declarations, in the arrivals board's split-flap style.
 * Before and during guessing: a countdown to when it opens, then to when it closes. Then the reveal, one step
 * at a time (a card's statements, then the lie and how many fell for it, then who declared it), then Smuggler of the
 * Year and Immigration Officer of the Year. The host steps it from their phone (/fly/custom-declaration-result-remote), from /fly/admin, or with the arrow keys here once the
 * admin key is entered on this screen. With the key, this screen also has all the host's buttons: extend the timer, end
 * it, show the result (start the reveal), Back and Next, and let guests see their results. Public and read-only otherwise; the backend only sends what's been revealed.
 * Built to run for hours: one poll at a time, cleaned up, and it reconnects by itself.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { KeyRound } from "lucide-react";
import { FlapGroup, STYLES, fit, useFlapSound, useWakeLock } from "./ArrivalsPage";
import { getLeaderboard, hostAction, setRemoteStep } from "./helpers/flyApi";
import { formatHms, formatTime, useNoIndex, useStoredKey, useTimeLeft } from "./helpers/flyPage";

const REVEAL_POLL_MS = 1000;
const HOLD_POLL_MS = 5000;
const MAX_RETRY_MS = 15000;
const NAME_WIDTH = 22;

const label = (number) => String(number).padStart(2, "0");

// The reveal's own styles, on top of the arrivals board's
const REVEAL_STYLES = `
.yj-arrivals .lb-statements{display:flex;flex-direction:column;gap:calc(var(--cw)*.4)}
.yj-arrivals .lb-statement{position:relative;font-family:var(--f-ui);font-weight:600;font-size:calc(var(--cw)*.95);line-height:1.25;color:var(--ink);
  padding:calc(var(--cw)*.35) calc(var(--cw)*.6);border:1px solid var(--line);border-radius:10px;background:var(--flap);transition:color .4s,opacity .4s}
.yj-arrivals .lb-statement.lie{color:var(--delayed)}
.yj-arrivals .lb-statement.lie .lb-text{text-decoration:line-through;text-decoration-thickness:.08em}
.yj-arrivals .lb-statement.truth{opacity:.55}
.yj-arrivals .lb-stamp{position:absolute;right:calc(var(--cw)*.6);top:50%;transform:translateY(-50%) rotate(-10deg);border:.12em solid var(--delayed);color:var(--delayed);
  border-radius:8px;padding:.05em .45em;font-family:var(--f-flap);font-size:calc(var(--cw)*.8);letter-spacing:.12em;animation:lb-stamp .45s cubic-bezier(.3,1.4,.5,1) both}
@keyframes lb-stamp{0%{transform:translateY(-50%) rotate(-10deg) scale(1.7);opacity:0}60%{transform:translateY(-50%) rotate(-10deg) scale(.95);opacity:1}100%{transform:translateY(-50%) rotate(-10deg) scale(1)}}
.yj-arrivals .lb-big{font-family:var(--f-flap);font-weight:600;font-size:calc(var(--cw)*1.2);letter-spacing:.08em;color:var(--landed);text-transform:uppercase}
.yj-arrivals .lb-note{font-family:var(--f-ui);font-size:calc(var(--cw)*.6);color:var(--ink-dim);letter-spacing:.12em;text-transform:uppercase}
.yj-arrivals .lb-clue{font-family:var(--f-ui);font-weight:600;font-size:calc(var(--cw)*1);line-height:1.3;color:var(--ink)}
@media (prefers-reduced-motion:reduce){.yj-arrivals .lb-stamp{animation:none}}
`;

/**
 * The board's data, polled. Faster during the reveal so the TV keeps up with the host. Recovers by itself.
 */
const useLeaderboard = () => {
    const [board, setBoard] = useState(null);
    const [offline, setOffline] = useState(false);
    const pokeRef = useRef(() => {});
    useEffect(() => {
        let stopped = false;
        let timer;
        let failures = 0;
        let revealing = false;
        const poll = async () => {
            clearTimeout(timer);
            try {
                const data = await getLeaderboard();
                if (stopped) return;
                failures = 0;
                revealing = data.phase === "closed" || data.phase === "finished";
                setBoard(data);
                setOffline(false);
            } catch {
                if (stopped) return;
                failures += 1;
                if (failures >= 3) setOffline(true);
            }
            const base = revealing ? REVEAL_POLL_MS : HOLD_POLL_MS;
            if (!stopped) timer = setTimeout(poll, Math.min(base * 2 ** Math.max(0, failures - 1), MAX_RETRY_MS));
        };
        pokeRef.current = poll;
        poll();
        return () => {
            stopped = true;
            clearTimeout(timer);
        };
    }, []);
    return { board, offline, refresh: () => pokeRef.current() };
};

/**
 * One card being revealed: its statements, then the lie, then who declared it
 */
const RevealCard = ({ card, title, click }) => (
    <div className="lb-statements">
        <div className="bhead">
            <span className="lb-big">{title || `Declaration No. ${label(card.number)}`}</span>
            {card.stage >= 1 && <span className="lb-note">{card.fellFor} {card.fellFor === 1 ? "person" : "people"} fell for it · {card.reports} {card.reports === 1 ? "report" : "reports"}</span>}
        </div>
        {card.statements.map((statement, index) => {
            const revealed = card.stage >= 1;
            const isLie = revealed && index === card.lie;
            return (
                <div key={index} className={`lb-statement ${isLie ? "lie" : revealed ? "truth" : ""}`}>
                    <span className="lb-text">{statement}</span>
                    {isLie && <span className="lb-stamp">SMUGGLED</span>}
                </div>
            );
        })}
        <div className="row" style={{ minHeight: "calc(var(--cw)*1.6)" }}>
            <span className="swatch" style={{ background: card.passenger?.colour || "transparent" }} />
            <FlapGroup text={fit(card.passenger ? card.passenger.name : "", NAME_WIDTH)} className="landed" click={click} />
            {card.passenger && <span className="lb-note">{card.passenger.city}</span>}
        </div>
    </div>
);

const LeaderboardPage = () => {
    const { board, offline, refresh } = useLeaderboard();
    const sound = useFlapSound();
    const [key, saveKey] = useStoredKey("yj030-admin-key");
    const [askingKey, setAskingKey] = useState(false);
    const [typedKey, setTypedKey] = useState("");
    const boardRef = useRef(null);

    useNoIndex("Unclaimed Declarations · YJ030");
    useWakeLock();

    const reveal = board?.reveal;

    // The arrow keys step the reveal, if this screen has the admin key
    const step = useCallback(async (by) => {
        if (!key || !reveal) return;
        try {
            await setRemoteStep(reveal.step + by, key);
            refresh();
        } catch (err) {
            if (err.status === 401) setAskingKey(true);
        }
    }, [key, reveal, refresh]);

    useEffect(() => {
        const onKey = (e) => {
            if (askingKey) return;
            if (e.key === "ArrowRight" || e.key === " " || e.key === "PageDown") { e.preventDefault(); step(1); }
            if (e.key === "ArrowLeft" || e.key === "PageUp") { e.preventDefault(); step(-1); }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [step, askingKey]);

    // The host's buttons on this screen: close (end the timer) | extend | start (show the result)
    const [acting, setActing] = useState(false);
    const act = async (action, question) => {
        if (question && !window.confirm(question)) return;
        setActing(true);
        try {
            await hostAction(action, key);
            refresh();
        } catch (err) {
            if (err.status === 401) setAskingKey(true);
            else window.alert(err.message);
        } finally {
            setActing(false);
        }
    };

    const phase = board?.phase;
    const open = phase === "open";
    const waitingForReveal = phase === "closed" && !reveal;
    const locked = phase === "locked";
    const left = useTimeLeft(open ? board?.closesAt : board?.opensAt, open || locked);
    const lastStep = reveal && reveal.step >= reveal.steps - 1;

    const fullScreen = () => {
        try {
            boardRef.current.requestFullscreen?.().catch(() => {});
        } catch {
            // Not available here
        }
    };

    const banner = reveal ? (reveal.smuggler ? "SMUGGLER OF THE YEAR" : reveal.officers ? "OFFICER OF THE YEAR" : "FINAL DESCENT · THE REVEAL")
        : waitingForReveal ? "IMMIGRATION CLOSED" : open ? "IMMIGRATION IS OPEN" : "IMMIGRATION OPENS SOON";

    return (
        <div className="yj-arrivals">
            <style>{STYLES}{REVEAL_STYLES}</style>
            <div className="wrap">
                <div className="board-scroll">
                    <section className="board" ref={boardRef} aria-label="Unclaimed Declarations board">
                        <div className="bhead">
                            <div className="btitle">
                                <div>
                                    <h1 className="big">UNCLAIMED DECLARATIONS</h1>
                                    <div className="sub">IMMIGRATION · BAH · RIGHT BROTHER AIRWAYS</div>
                                </div>
                            </div>
                            {reveal?.card && <span className="lbl">Card {reveal.cardIndex + 1} of {reveal.cardCount}</span>}
                        </div>

                        <div className="row banner">
                            <span className="swatch" />
                            <FlapGroup text={fit(banner, 29)} click={sound.click} />
                        </div>

                        {!board && <p className="lb-note">Connecting…</p>}

                        {board && waitingForReveal && (
                            <div className="lb-statements">
                                <p className="lb-clue">Reports are in. The results are coming up…</p>
                            </div>
                        )}

                        {board && !reveal && !waitingForReveal && (
                            <div className="lb-statements">
                                {open && left !== null && <span className="lb-big">Closes in {formatHms(left)}</span>}
                                {locked && left !== null && <span className="lb-big">Opens in {formatHms(left)}</span>}
                                <p className="lb-clue">Scan a card on the wall, name the passenger, spot the lie.</p>
                                {open && (
                                    <p className="lb-note">{board.reports} {board.reports === 1 ? "report" : "reports"} filed by {board.officers} {board.officers === 1 ? "officer" : "officers"}</p>
                                )}
                            </div>
                        )}

                        {reveal?.card && <RevealCard card={reveal.card} click={sound.click} />}
                        {reveal?.smuggler && <RevealCard card={reveal.smuggler} title={`No. ${label(reveal.smuggler.number)} fooled the most`} click={sound.click} />}
                        {reveal?.officers && (
                            <div className="lb-statements">
                                <span className="lb-big">Immigration Officer of the Year</span>
                                {reveal.officers.length === 0 && <p className="lb-clue">No reports were filed.</p>}
                                {reveal.officers.map((officer, index) => (
                                    <div key={officer.name} className="row">
                                        <span className="swatch" style={{ background: officer.colour }} />
                                        <FlapGroup text={fit(officer.name, NAME_WIDTH)} className={index === 0 ? "landed" : ""} baseDelay={index * 300} click={sound.click} />
                                        <FlapGroup text={fit(`${officer.points} PTS`, 7)} className={index === 0 ? "landed" : ""} baseDelay={index * 300 + 200} click={sound.click} />
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="bfoot">
                            <span>{reveal ? `Step ${reveal.step + 1} of ${reveal.steps}` : open ? `Reports close at ${formatTime(board.closesAt)}` : locked && board.opensAt ? `Opens at ${formatTime(board.opensAt)}` : "Reports close at Final Descent"}{offline && <span className="offline"> · Reconnecting…</span>}</span>
                            <span>{reveal && key ? "← → to step" : ""}</span>
                        </div>
                    </section>
                </div>

                <div className="btns">
                    <button type="button" onClick={sound.toggle} aria-pressed={sound.on}>Flap sound: {sound.on ? "on" : "off"}</button>
                    <button type="button" onClick={fullScreen}>Full screen</button>
                    {key && (open || waitingForReveal) && (
                        <button type="button" disabled={acting} onClick={() => act("extend")}>Extend timer +5 min</button>
                    )}
                    {key && open && (
                        <button type="button" disabled={acting} onClick={() => act("close", "End the timer now? Nobody can file or change a report after this.")}>End timer</button>
                    )}
                    {key && waitingForReveal && (
                        <button type="button" disabled={acting} onClick={() => act("start")}>Show the result</button>
                    )}
                    {key && reveal && (
                        <>
                            <button type="button" disabled={reveal.step === 0} onClick={() => step(-1)}>Back</button>
                            {!lastStep && <button type="button" onClick={() => step(1)}>Next</button>}
                        </>
                    )}
                    {key && lastStep && phase === "closed" && (
                        <button type="button" disabled={acting} onClick={() => act("finish", "Show guests their results? They see the answers and their scores.")}>Show guests their results</button>
                    )}
                    {!key && <button type="button" onClick={() => setAskingKey(true)}><KeyRound size={14} style={{ display: "inline", marginRight: 6 }} aria-hidden="true" />Host controls</button>}
                </div>
                {askingKey && (
                    <form className="btns" onSubmit={(e) => { e.preventDefault(); if (typedKey.trim()) { saveKey(typedKey.trim()); setAskingKey(false); setTypedKey(""); } }}>
                        <input type="password" value={typedKey} onChange={(e) => setTypedKey(e.target.value)} placeholder="Admin key" aria-label="Admin key" autoFocus
                            style={{ background: "var(--hall)", color: "var(--ink)", border: "1px solid var(--line)", borderRadius: 6, padding: "12px 14px", fontSize: 16 }} />
                        <button type="submit">Unlock host controls</button>
                        <button type="button" onClick={() => setAskingKey(false)}>Cancel</button>
                    </form>
                )}
            </div>
        </div>
    );
};

export default LeaderboardPage;
